const RUNTIME_API_BASE: string = (() => {
  if (typeof window === "undefined") return "";
  const host = window.location.hostname;
  if (host === "dev.portal.ernsthaus.de" || host === "www.dev.portal.ernsthaus.de") {
    return "https://dev.api.ernsthaus.de";
  }
  if (host === "portal.ernsthaus.de" || host === "www.portal.ernsthaus.de") {
    return "https://api.ernsthaus.de";
  }
  // Lokaler Vite-Dev: Requests über Proxy (/auth → DEV_API)
  return "";
})();

export const API_BASE = RUNTIME_API_BASE;

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

function buildUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  if (!API_BASE) return path;
  return `${API_BASE}${path.startsWith("/") ? "" : "/"}${path}`;
}

async function parseBody(res: Response): Promise<unknown> {
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    try {
      return await res.json();
    } catch {
      return null;
    }
  }
  try {
    return await res.text();
  } catch {
    return null;
  }
}

export async function apiRequest<T>(
  method: string,
  path: string,
  json?: unknown,
): Promise<T> {
  const headers = new Headers();
  let body: string | undefined;
  if (json !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(json);
  }

  let res: Response;
  try {
    res = await fetch(buildUrl(path), {
      method,
      credentials: "include",
      headers,
      body,
    });
  } catch {
    throw new ApiError(0, "Netzwerkfehler: Server nicht erreichbar.");
  }

  if (!res.ok) {
    const parsed = await parseBody(res);
    let message = `${res.status} ${res.statusText}`;
    if (parsed && typeof parsed === "object" && "detail" in parsed) {
      const detail = (parsed as { detail?: unknown }).detail;
      if (typeof detail === "string") message = detail;
    }
    throw new ApiError(res.status, message, parsed);
  }

  if (res.status === 204) return undefined as T;
  return (await parseBody(res)) as T;
}

export const api = {
  get: <T>(path: string) => apiRequest<T>("GET", path),
  post: <T>(path: string, json?: unknown) => apiRequest<T>("POST", path, json),
};

async function fetchAuthenticatedBlob(path: string): Promise<Blob> {
  let res: Response;
  try {
    res = await fetch(buildUrl(path), {
      method: "GET",
      credentials: "include",
    });
  } catch {
    throw new ApiError(0, "Netzwerkfehler: Server nicht erreichbar.");
  }
  if (!res.ok) {
    const parsed = await parseBody(res);
    let message = `${res.status} ${res.statusText}`;
    if (parsed && typeof parsed === "object" && "detail" in parsed) {
      const detail = (parsed as { detail?: unknown }).detail;
      if (typeof detail === "string") message = detail;
    }
    throw new ApiError(res.status, message, parsed);
  }
  return res.blob();
}

/** Authenticated GET → object URL (caller must revoke). */
export async function fetchAuthenticatedBlobUrl(path: string): Promise<string> {
  const blob = await fetchAuthenticatedBlob(path);
  return URL.createObjectURL(blob);
}

/** PDF mit Session-Cookie laden und als Blob-URL öffnen. */
export async function openAuthenticatedPdf(path: string): Promise<void> {
  const blob = await fetchAuthenticatedBlob(path);
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener,noreferrer");
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
